import { useState } from 'react';
import { Quote } from 'lucide-react';

interface Testimonial {
  slug: string;
  name: string;
  role: string;
  agency: string;
  quote: string;
  initials: string;
  // Stock portrait URL. pravatar.cc serves deterministic 600×600
  // portraits keyed by ?img=N — high enough resolution for the 4:5
  // crop without shipping images. Swap to real partner photos when
  // they're approved.
  photoUrl: string;
  // Tailwind gradient classes, used if the stock URL ever 404s so
  // the card still looks intentional.
  fallbackGradient: string;
}

const TESTIMONIALS: Testimonial[] = [
  {
    slug: 'maren',
    name: 'Maren Kessler',
    role: 'Strategy Lead',
    agency: 'Northwave Studio',
    quote:
      'We used to brief weeks of media planning to land a single launch beat. Now we design the moment, and the placement bends to it.',
    initials: 'MK',
    photoUrl: 'https://i.pravatar.cc/600?img=47',
    fallbackGradient: 'from-primary-soft via-info-100/60 to-warning-100/40',
  },
  {
    slug: 'devan',
    name: 'Devan Aldridge',
    role: 'Growth Director',
    agency: 'Quillpath Agency',
    quote:
      'The reporting alone replaced three internal dashboards. Our analysts spend their week on insights, not pipelines.',
    initials: 'DA',
    photoUrl: 'https://i.pravatar.cc/600?img=12',
    fallbackGradient: 'from-success-100/70 via-primary-soft to-info-100/50',
  },
  {
    slug: 'saoirse',
    name: 'Saoirse Halloran',
    role: 'Brand Partnerships',
    agency: 'Forenote Co.',
    quote:
      'It feels less like buying inventory and more like producing a campaign — the team behind it actually cares what runs.',
    initials: 'SH',
    photoUrl: 'https://i.pravatar.cc/600?img=45',
    fallbackGradient: 'from-warning-100/60 via-secondary-soft to-primary-soft',
  },
];

export function Testimonials() {
  return (
    <section
      id="testimonials"
      aria-labelledby="testimonials-heading"
      className="border-y border-border-soft bg-surface"
    >
      <div className="mx-auto max-w-container-lg px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-xs font-semibold uppercase tracking-caps text-primary">
            Trusted by agencies
          </span>
          <h2
            id="testimonials-heading"
            className="mt-3 font-display text-4xl leading-display tracking-display text-ink sm:text-5xl"
          >
            What partners say after working with us.
          </h2>
          <p className="mt-5 text-lg text-ink-secondary sm:text-xl">
            Real teams, real campaigns. We let our partners describe the work in their own words.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 sm:gap-7 md:grid-cols-3">
          {TESTIMONIALS.map((t) => (
            <TestimonialCard key={t.slug} testimonial={t} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialCard({ testimonial: t }: { testimonial: Testimonial }) {
  const [imgFailed, setImgFailed] = useState(false);
  return (
    <figure className="relative flex flex-col overflow-hidden rounded-lg border border-border-soft bg-surface shadow-xs transition-shadow duration-base hover:shadow-md">
      <div className="relative aspect-[4/5] w-full overflow-hidden bg-surface-sunken">
        {imgFailed ? (
          <div
            className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br ${t.fallbackGradient}`}
            aria-hidden
          >
            <span className="font-display text-6xl text-ink/30 tracking-display">
              {t.initials}
            </span>
          </div>
        ) : (
          <img
            src={t.photoUrl}
            alt={`${t.name}, ${t.role} at ${t.agency}`}
            loading="lazy"
            referrerPolicy="no-referrer"
            className="absolute inset-0 h-full w-full object-cover object-top"
            onError={() => setImgFailed(true)}
          />
        )}
        <span
          className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-surface/90 text-primary shadow-xs backdrop-blur"
          aria-hidden
        >
          <Quote className="h-4 w-4" />
        </span>
      </div>
      <blockquote className="flex flex-1 flex-col gap-5 p-7 sm:p-8">
        <p className="text-lg leading-body text-ink sm:text-xl">&ldquo;{t.quote}&rdquo;</p>
        <figcaption className="mt-auto border-t border-border-soft pt-5">
          <div className="text-base font-semibold text-ink">{t.name}</div>
          <div className="mt-1 text-sm text-ink-secondary">
            {t.role} · {t.agency}
          </div>
        </figcaption>
      </blockquote>
    </figure>
  );
}
